import { ProfileRoles } from '@bp/pim-auth-constants';
import { AddIcon, CheckIcon, Grid, GridColumn, GridRow, Modal, SpinnerIcon } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { BpSubpage } from 'components/BpSubpage/BpSubpage';
import { TodayEventList } from 'components/Events/components/TodayEventList';
import { MessageLinkButtons } from 'components/LinkButtons/MessageLinkButtons';
import { GroupMessageModal } from 'components/Messages/modals/GroupMessageModal';
import dayjs from 'dayjs';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { NotificationCountType } from 'matrix-js-sdk';
import { type FC, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { CONNECT_DATE_TEMPLATE } from 'utils/connectLib';
import { AnnouncementForm, type AnnouncementFormType } from '../../../components/AnnouncementForm/AnnouncementForm';
import { AnnouncementsList } from '../../../components/AnnouncementsList/AnnouncementsList';
import { BpCard } from '../../../components/BpCard/BpCard';
import { WorkMaterialsList } from '../../../components/WorkMaterialsList/WorkMaterialsList';
import { useGetAnnouncements } from '../../../hooks/matrix/useGetAnnouncements';
import { useGetMatrixRoomByName } from '../../../hooks/matrix/useGetMatrixRoomByName';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useTechnicalMessages } from '../../../hooks/useTechnicalMessages';
import { useMessageCommands } from '../../../matrix/commands/useMessageCommands';
import { redirectToDmMessagePage } from '../../../matrix/utils/redirectToDmMessagePage';
import { backendApi } from '../../../utils/backendApi';
import { NotificationReason, RoomNames } from '../../../utils/matrixClient';
import { showErrorToast } from '../../../utils/showErrorToast';

const GroupOverviewSubpagePageQueryDocument = graphql(`
  query GroupOverviewSubpagePageData($groupUuid: ID!, $eventWhere: CalendarEventWhere!) {
    groups(where: { uuid: $groupUuid }) {
      __typename
      uuid
      name
      organization {
        __typename
        uuid
      }
      teachingUnits {
        __typename
        uuid
        workMaterials {
          __typename
          uuid
          ...WorkMaterialsListFields
        }
      }
    }
    birthdayEvents(groupUuid: $groupUuid) {
      __typename
      uuid
      color
      start
      duration
      virtualLocations {
        __typename
        uuid
      }
    }
    ...TodayEventListFields
    ...MessageLinkButtonsFields
    ...GroupMessageFields
  }
`);

export const GroupOverviewSubpage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pimAuthClaims } = useAuthClaims();

  const { groupUuid: paramUuid } = useParams();
  const groupUuid = paramUuid ?? '';

  const { technicalMessages } = useTechnicalMessages();
  const { getRoom } = useGetMatrixRoomByName();
  const { markAsRead } = useMessageCommands();
  const { client } = useMatrixClient();

  const [announcementModalOpen, setAnnouncementModalOpen] = useState<boolean>(false);
  const [messageModalOpen, setMessageModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [{ data }, refetch] = useQuery({
    query: GroupOverviewSubpagePageQueryDocument,
    variables: {
      groupUuid: groupUuid,
      eventWhere: {
        AND: [
          { holderConnection: { Group: { node: { uuid: groupUuid } } } },
          { days_SOME: { date_CONTAINS: dayjs().format(CONNECT_DATE_TEMPLATE) } },
        ],
      },
    },
    pause: !groupUuid,
  });

  const currentGroup = data?.groups?.[0];
  const workMaterial = currentGroup?.teachingUnits[0]?.workMaterials;
  const room = getRoom(`${currentGroup?.organization.uuid}_${groupUuid}`, RoomNames.Group);
  const hasUnread = room ? room.getRoomUnreadNotificationCount(NotificationCountType.Total) > 0 : false;
  const announcements = useGetAnnouncements(room);

  const groupSubject = {
    uuid: groupUuid,
    organization: { uuid: currentGroup?.organization.uuid ?? '' },
  };

  const perms = usePermissionChecker();

  const canCreateGroupAnnouncement = perms?.canCreateGroupAnnouncement(groupSubject);

  // this detects if the group has started or ended conferences and updates the „SofortKonferenz“ Button
  // biome-ignore lint/correctness/useExhaustiveDependencies(refetch): ignore
  useEffect(() => {
    let count = 0;
    if (technicalMessages.length > 0)
      technicalMessages.forEach((message) => {
        try {
          if (message.content != null) {
            const content = JSON.parse(message.content);
            if (
              (message.groupUuid === groupUuid && content.type === NotificationReason.GroupEventCreated) ||
              content.type === NotificationReason.GroupEventEnded
            ) {
              count++;
            }
          }
        } catch {
          // silence is golden
        }
      });

    // this serves to limit the initial refetchs
    if (count > 0) {
      refetch({ requestPolicy: 'cache-and-network' });
    }
  }, [technicalMessages, groupUuid]);

  function handleMessageAll() {
    setMessageModalOpen(true);
  }

  async function handleMessage(uuid: string, role: ProfileRoles) {
    await redirectToDmMessagePage({
      groupUuid: groupUuid,
      targetProfileUuid: uuid,
      targetProfileOrganizationUuid: '',
      targetProfileRole: role,
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
      context: 'group',
      permissionChecker: perms,
      navigate,
    });
  }

  const handleAnnouncementFormSubmit = async (values: AnnouncementFormType) => {
    if (values) {
      try {
        setLoading(true);
        const roomId = room?.summary?.roomId;

        if (!roomId) {
          throw new Error('Could not find room');
        }

        if (!client) {
          throw new Error('Could not get Matrix Client');
        }

        if (!currentGroup?.name) {
          throw new Error('Could not get groupname');
        }

        const resp = await backendApi.notify(
          {
            type: NotificationReason.GroupAnnouncement,
            content: values.message,
            title: values.title,
            groupUuid,
          },
          { roomId, groupName: currentGroup?.name, client },
        );

        if (!resp.ok) throw new Error(resp.statusText);

        setAnnouncementModalOpen(false);
      } catch (error) {
        showErrorToast({
          name: '',
          graphQLErrors: [],
          message: error as string,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <BpSubpage>
      <Grid>
        <GridRow mobileGap='var(--grid-column-gap)'>
          <GridColumn width={8}>
            <GridRow mobileGap='var(--grid-column-gap)' direction='column' spacingTop='none'>
              <BpCard
                canScroll
                fixedHeight='374px'
                noPadding
                header={{
                  headline: t('workmaterials.title'),
                  actions: perms?.canCreateBpGroupMaterial({
                    uuid: groupUuid,
                    organization: {
                      uuid: currentGroup?.organization.uuid ?? '',
                    },
                  })
                    ? [
                        {
                          text: t('workmaterials.addMaterial'),
                          icon: <AddIcon />,
                          callback: () => navigate(`/groups/${groupUuid}/workmaterials/add`),
                        },
                      ]
                    : undefined,
                }}
              >
                <WorkMaterialsList
                  workMaterial={workMaterial}
                  onNavigate={(uuid) => navigate(`/groups/${groupUuid}/workmaterials/${uuid}`)}
                  isGroupEditor={
                    !!perms?.isGroupEditor({
                      uuid: groupUuid,
                      organization: {
                        uuid: currentGroup?.organization.uuid ?? '',
                      },
                    })
                  }
                />
              </BpCard>
            </GridRow>
            <GridRow>
              <BpCard
                canScroll
                fixedHeight='374px'
                noPadding
                header={{
                  headline: t('announcements.title'),
                  actions: [
                    {
                      text: t('messages.markAsRead'),
                      hierarchy: 'ghost',
                      icon: <CheckIcon />,
                      callback: () => {
                        markAsRead(room?.roomId ?? '');
                      },
                      disabled: !hasUnread || !canCreateGroupAnnouncement,
                    },
                    {
                      text: t('announcements.new'),
                      icon: <AddIcon />,
                      callback: () => {
                        setAnnouncementModalOpen(true);
                      },
                      disabled: !canCreateGroupAnnouncement,
                    },
                  ],
                }}
              >
                <AnnouncementsList
                  announcements={announcements}
                  hasPermissionToDelete={canCreateGroupAnnouncement ?? false}
                />
              </BpCard>
            </GridRow>
          </GridColumn>
          <GridColumn width={4}>
            <GridRow spacingTop='none'>
              <TodayEventList
                query={data}
                permissionChecker={perms}
                refetchData={() => refetch({ requestPolicy: 'network-only' })}
              />
            </GridRow>
            <GridRow>
              <MessageLinkButtons onMessageAll={handleMessageAll} onMessage={handleMessage} query={data} />
            </GridRow>
          </GridColumn>
        </GridRow>
      </Grid>

      {room && (
        <Modal
          isOpen={announcementModalOpen}
          onRequestClose={() => setAnnouncementModalOpen(false)}
          isFormModal
          hideFooter
          title={t('announcements.new')}
          width='s'
        >
          <Suspense fallback={<SpinnerIcon className={'svg-icon large spinning'} />}>
            <AnnouncementForm
              onSubmit={handleAnnouncementFormSubmit}
              loading={loading}
              closeModal={() => setAnnouncementModalOpen(false)}
            />
          </Suspense>
        </Modal>
      )}

      <GroupMessageModal
        isOpen={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        groupUuid={groupUuid}
        query={data}
      />
    </BpSubpage>
  );
};
